import { Injectable, signal } from '@angular/core';
import { ToastMessage, ToastOptions } from './toast.models';

const DEFAULT_OPTIONS: ToastOptions = {
    life: 3000,
    sticky: false,
    closable: false,
    position: 'top-center',
    progressBar: false,
};

/**
 * Service for managing toast notifications in the application.
 * Provides methods to show different types of toast messages (success, error, info, warn)
 * with customizable options.
 */
@Injectable({
    providedIn: 'root',
})
export class ToastService {
    private toasts = signal<ToastMessage[]>([]);
    /** Observable of current toast messages */
    toasts$ = this.toasts.asReadonly();

    /**
     * Removes all toast messages from the display
     */
    private clear() {
        this.toasts.set([]);
    }

    /**
     * Adds a new toast message to the display
     * @param message The toast message configuration to add
     */
    private add(message: ToastMessage) {
        const id = crypto.randomUUID();
        const newToast = {
            ...message,
            id,
            options: { ...DEFAULT_OPTIONS, ...message.options },
        };

        // Check for duplicates if preventDuplicate is enabled
        if (newToast.options?.preventDuplicate) {
            const isDuplicate = this.toasts().some((toast) => toast.detail === newToast.detail && toast.severity === newToast.severity);
            if (isDuplicate) {
                return;
            }
        }

        this.toasts.update((currentToasts) => [...currentToasts, newToast]);

        if (!newToast.options?.sticky && newToast.options?.life) {
            setTimeout(() => this.remove(id), newToast.options.life);
        }
    }

    /**
     * Removes a specific toast message by its ID
     * @param id The unique identifier of the toast to remove
     */
    remove(id: string) {
        this.toasts.update((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
    }

    /**
     * Displays a toast message with the specified configuration
     * @param message The toast message configuration to show
     */
    show(message: ToastMessage) {
        this.add(message);
    }

    /**
     * Displays a success toast message
     * @param detail The main message content
     * @param summary Optional summary text shown as a title
     * @param options Optional configuration for the toast display
     */
    success(detail: string, summary?: string, options?: ToastOptions) {
        this.add({ severity: 'success', detail, summary, options });
    }

    /**
     * Displays an error toast message
     * @param detail The main message content
     * @param summary Optional summary text shown as a title
     * @param options Optional configuration for the toast display
     */
    error(detail: string, summary?: string, options?: ToastOptions) {
        this.add({ severity: 'error', detail, summary, options });
    }

    /**
     * Displays an info toast message
     * @param detail The main message content
     * @param summary Optional summary text shown as a title
     * @param options Optional configuration for the toast display
     */
    info(detail: string, summary?: string, options?: ToastOptions) {
        this.add({ severity: 'info', detail, summary, options });
    }

    /**
     * Displays a warning toast message
     * @param detail The main message content
     * @param summary Optional summary text shown as a title
     * @param options Optional configuration for the toast display
     */
    warn(detail: string, summary?: string, options?: ToastOptions) {
        this.add({ severity: 'warn', detail, summary, options });
    }
}
